<template>
  <div class="account-admin">
    <div v-if="isSelfRef" class="admin-setting-group">
      <h5>Privileges</h5>
      <ul class="list-unstyled">
        <li v-for="pr in privileges" :key="pr">
          <label>
            <input type="checkbox" :checked="tokenManager.current.hasPrivilege(pr)" disabled />
            {{ getPrivilegeDisplayName(pr) }}
          </label>
        </li>
      </ul>
    </div>
    <div class="admin-setting-group">
      <h5>Metadata</h5>
      <label>
        <input type="checkbox" v-model="nameConfirmedRef" :disabled="!canEditRef" />
        User name confirmed
      </label>
    </div>
    <div class="admin-setting-group limits">
      <h5>Limits</h5>
      <ul class="list-unstyled">
        <li v-for="limit in limitKeysRef" :key="limit">
          {{ capitalize(limit) }}: <span class="big-value">{{ userRef!.limits![limit] }}</span>
        </li>
      </ul>
    </div>
    <div class="admin-setting-group">
      <h5>Account options</h5>
      <label>
        <input type="checkbox" v-model="limitsDisabledRef" :disabled="!canEditRef" />
        Disable account limits
      </label>
    </div>
    <div class="admin-actions">
      <button type="button" v-if="showImpersonateRef" class="btn btn-warning impersonate" @click="impersonateUser">
        <ShieldIcon size="1.2x"/>
        Impersonate
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.account-admin {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.admin-setting-group {
  label {
    margin-left: 1rem;
  }

  &.limits {
    ul {
      margin-left: 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      align-items: flex-start;

      li {
        display: flex;
        align-items: baseline;        
      }

      .big-value {
        margin-left: 0.5rem;
        font-size: 1.3rem;
      }
    }
  }
}

.admin-actions {
  margin-top: 1rem;

  .impersonate svg {
    margin-top: -2px;
    margin-right: 0.25rem;
    fill: var(--bs-warning);
    filter: contrast(0.7);
  }
}
</style>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useUserStore } from '@/gp/UserStore'
import { LoadMode } from '@/util/AsyncData'
import { UserPrivilege, getPrivilegeDisplayName, useTokenManager } from '@/auth/TokenManager'
import { PatchChange } from '@/util/Api'
import { ShieldIcon } from '@zhuowenli/vue-feather-icons'
import { UserLimits } from '@/gp/GroupAdminModel'
import { capitalize } from '@/util/LanguageUtil'

const props = defineProps<{
  userId?: string
}>()

const privileges = [
  UserPrivilege.ReadAllUsers,
  UserPrivilege.ReadAllDataGroups,
  UserPrivilege.ReadAllFamilyGroups,
  UserPrivilege.ManageUsers,
  UserPrivilege.ImpersonateUser,
]

const nameConfirmedRef = ref(false)
const limitsDisabledRef = ref(false)
const busyRef = ref(false)

const userStore = useUserStore()
const tokenManager = useTokenManager()

const isSelfRef = computed(() => tokenManager.current.userId == props.userId)
const userRef = computed(() => userStore.getAsyncUser(props.userId, LoadMode.EnsureLoaded)?.data)
const limitKeysRef = computed(() => Object.keys(new UserLimits()) as (keyof UserLimits)[])
const canEditRef = computed(() => tokenManager.current.hasPrivilege(UserPrivilege.ManageUsers) && !busyRef.value)
const showImpersonateRef = computed(() => !isSelfRef.value && tokenManager.canImpersonateAnyUser())

watch(userRef, () => {
  nameConfirmedRef.value = userRef.value?.metadata?.nameConfirmed ?? false
  limitsDisabledRef.value = userRef.value?.limitsDisabled ?? false
}, { immediate: true })

watch(nameConfirmedRef, async () => {
  if (userRef.value && nameConfirmedRef.value != userRef.value.metadata?.nameConfirmed) {
    busyRef.value = true
    const change = new PatchChange('/metadata/nameConfirmed', nameConfirmedRef.value)
    await userStore.patchAsync(props.userId!, [change])
    busyRef.value = false
  }
})

watch(limitsDisabledRef, async () => {
  if (userRef.value && limitsDisabledRef.value != userRef.value?.limitsDisabled) {
    busyRef.value = true
    await userStore.setLimitsDisabledAsync(props.userId!, limitsDisabledRef.value)
    busyRef.value = false
  }
})

async function impersonateUser() {
  if (props.userId) {
    tokenManager.impersonateAsync(props.userId)
  }
}
</script>
